import React, { useState, useEffect, useRef } from "react";
import Wrapper from "../../components/UI/Wrapper";
import axios from "axios";
import "../../components/LeadPaymentDetails/LeadPaymentDetails.css";
import "react-toastify/dist/ReactToastify.css";
import JSZip from "jszip";
import "../../components/AccountsBillingDetails/AccountsBillingDetails.css";

// NOTE: This component now accepts `quotation_amount` (number) prop to determine PAN requirement.
// Usage: <AccountsBillingDetails Relocation_Id={relocationId} quotation_amount={someNumber} />

const AccountsBillingDetails = ({ Relocation_Id, quotation_amount = 0 }) => {
  const baseAPI = process.env.REACT_APP_API_URL;
  const baseImageURL = process.env.REACT_APP_IMAGE_URL;
  const [billingDetailResponse, setbillingDetailResponse] = useState({});
  const [loading, setLoading] = useState(false);
  const [showGstFields, setShowGstFields] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [prevGst, setPrevGst] = useState(null); // used to preserve GST values when toggling
  const fileInputRef_PanCard = useRef(null);
  const fileInputRef_GST = useRef(null);

  // Fetch rows data from the API
  useEffect(() => {
    if (Relocation_Id) getDocument(Relocation_Id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Relocation_Id]);

  async function getDocument(Relocation_Id) {
    const storedToken = JSON.parse(localStorage.getItem("token"));
    if (storedToken) {
      try {
        const response = await axios.get(
          `${baseAPI}userget/GetBillingDetailInfo?relocation_id=` + Relocation_Id,
          {
            headers: {
              Authorization: `Bearer ${storedToken}`,
            },
          }
        );

        const data = response.data.data || [];
        const first = data[0] || null;

        if (first) {
          // Keep response for links and original values
          setbillingDetailResponse(first);

          setFirstItem((prev) => ({
            billingInfo_id: first.billingInfo_id || "",
            relocation_id: first.relocation_id || Relocation_Id,
            billingName: first.billingName || "",
            contactNumber: first.contactNumber || "",
            billingEmail: first.billingEmail || "",
            panNumber: first.panNumber || "",
            panDocPath: first.panDocPath || "",
            panDocName: first.panDocName || "", // note: server returns file name string
            gst_number: first.gst_number || "",
            gstDocPath: first.gstDocPath || "",
            gstDocName: first.gstDocName || "",
          }));

          // show GST fields if existing value exists
          setShowGstFields(!!first.gst_number || !!first.gstDocName);

          // default editing false (view mode)
          setIsEditing(false);
        } else {
          // no data
          setbillingDetailResponse({});
          setFirstItem({
            billingInfo_id: "",
            relocation_id: Relocation_Id,
            billingName: "",
            contactNumber: "",
            billingEmail: "",
            panNumber: "",
            panDocPath: "",
            panDocName: "",
            gst_number: "",
            gstDocPath: "",
            gstDocName: "",
          });

          setShowGstFields(false);
          setIsEditing(true); // allow creating new record
        }
      } catch (error) {
        console.error("Error fetching user detail:", error);
      }
    }
  }

  const [firstItem, setFirstItem] = useState({
    billingInfo_id: "",
    relocation_id: "",
    billingName: "",
    contactNumber: "",
    billingEmail: "",
    panNumber: "",
    panDocPath: "",
    panDocName: "",
    gst_number: "",
    gstDocPath: "",
    gstDocName: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFirstItem((prevItem) => ({
      ...prevItem,
      [name]: value,
    }));
  };

  function handleChangeDoc(e) {
    const { name, files } = e.target;

    if (name === "panDocName" || name === "gstDocName") {
      const file = files && files[0];
      if (!file) return;

      if (file.size > 50 * 1024 * 1024) {
        alert("File size exceeds 50 MB limit.");
        e.target.value = null;
        return;
      }

      if (isValidFileType(file)) {
        if (
          file.type === "application/zip" ||
          file.type === "application/x-zip-compressed"
        ) {
          validateZipFile(file, name, e);
        } else {
          getUpdatedImage(file, name);
        }
      } else {
        alert("Unsupported file type. Please upload a valid file.");
        e.target.value = null;
        return;
      }
    }
  }

  function getUpdatedImage(file, name) {
    if (file) {
      setFirstItem((prev) => ({
        ...prev,
        [name]: file, // store File object for upload
      }));
    }
  }

  function isValidFileType(file) {
    const allowedTypes = [
      "image/jpeg",
      "image/png",
      "application/pdf",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
      "video/mp4",
      "video/x-msvideo",
      "video/x-matroska",
      "video/quicktime",
      "application/zip",
      "application/x-zip-compressed",
    ];
    return file && allowedTypes.includes(file.type);
  }

  function validateZipFile(file, name, e) {
    const reader = new FileReader();
    reader.onload = function (event) {
      const zipData = event.target.result;

      try {
        const jszip = new JSZip();
        jszip.loadAsync(zipData).then((zip) => {
          const allowedExtensions = ["jpg", "png", "pdf", "docx", "xlsx"];
          let isValid = true;

          zip.forEach((relativePath) => {
            const extension = relativePath.split(".").pop().toLowerCase();
            if (!allowedExtensions.includes(extension)) {
              isValid = false;
            }
          });

          if (isValid) {
            getUpdatedImage(file, name);
          } else {
            alert("ZIP file contains unsupported file types.");
            e.target.value = null;
          }
        });
      } catch (error) {
        alert("Error reading ZIP file.");
        e.target.value = null;
      }
    };

    reader.readAsArrayBuffer(file);
  }

  // Helper to determine if PAN is required by quotation amount
  const isPanRequiredByAmount = () => {
    const amt = Number(quotation_amount || billingDetailResponse?.quotation_amount || 0);
    return !isNaN(amt) && amt > 200000;
  };

  const validateBeforeSubmit = (isUpdate = false) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(firstItem.billingEmail)) {
      alert("Please enter a valid email address.");
      return false;
    }

    // PAN mandatory based on quotation amount
    if (isPanRequiredByAmount()) {
      if (!firstItem.panNumber) {
        alert("PAN Number is mandatory for quotation amount > 200,000.");
        return false;
      }

      // For new uploads, ensure file provided. For update, either existing string (from server) or File instance is ok
      const panProvided = firstItem.panDocName instanceof File || (!!firstItem.panDocName && typeof firstItem.panDocName === "string");
      if (!panProvided) {
        alert("Please upload PAN Card document (required for high quotation amounts).");
        return false;
      }
    }

    // GST validation if GST section visible
    if (showGstFields) {
      if (!firstItem.gst_number) {
        alert("Please enter GST Number.");
        return false;
      }

      const gstProvided = firstItem.gstDocName instanceof File || (!!firstItem.gstDocName && typeof firstItem.gstDocName === "string");
      if (!gstProvided) {
        alert("Please upload GST Document.");
        return false;
      }
    }

    return true;
  };

  const uploadDocument = async (e) => {
    e.preventDefault();
    if (!validateBeforeSubmit(false)) return;

    setLoading(true);

    const formData = new FormData();
    formData.append("relocation_id", Relocation_Id);
    formData.append("billingName", firstItem.billingName || "");
    formData.append("contactNumber", firstItem.contactNumber || "");
    formData.append("billingEmail", firstItem.billingEmail || "");
    formData.append("panNumber", firstItem.panNumber || "");
    formData.append("gst_number", firstItem.gst_number || "");

    // Files - ensure we append File objects
    if (firstItem.panDocName instanceof File) formData.append("panDocName", firstItem.panDocName);
    if (firstItem.gstDocName instanceof File) formData.append("gstDocName", firstItem.gstDocName);

    try {
      const storedToken = JSON.parse(localStorage.getItem("token"));
      await axios.post(`${baseAPI}userpost/InsertBillingDetails`, formData, {
        headers: {
          Authorization: `Bearer ${storedToken}`,
          "Content-Type": "multipart/form-data",
        },
      });

      alert("Billing Details added successfully");
      await getDocument(Relocation_Id);
    } catch (error) {
      console.error("Error posting data:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateDetails = async (e) => {
    e.preventDefault();
    if (!validateBeforeSubmit(true)) return;

    setLoading(true);
    const formData = new FormData();
    formData.append("billingInfo_id", firstItem.billingInfo_id || "");
    formData.append("relocation_id", Relocation_Id);
    formData.append("gst_number", firstItem.gst_number || "");

    // Append files only if they're File instances (newly uploaded)
    if (firstItem.panDocName instanceof File) {
      formData.append("panDocName", firstItem.panDocName);
    }

    if (firstItem.gstDocName instanceof File) {
      formData.append("gstDocName", firstItem.gstDocName);
    }

    // Send existing file names so the server can keep them if no new upload
    formData.append("existingPanDocName", typeof firstItem.panDocName === "string" ? firstItem.panDocName : "");
    formData.append("existingGstDocName", typeof firstItem.gstDocName === "string" ? firstItem.gstDocName : "");

    try {
      const storedToken = JSON.parse(localStorage.getItem("token"));
      await axios.post(`${baseAPI}userpost/UpdateBillingDetails`, formData, {
        headers: {
          Authorization: `Bearer ${storedToken}`,
          "Content-Type": "multipart/form-data",
        },
      });

      alert("Billing Details updated successfully");
      await getDocument(Relocation_Id);
    } catch (error) {
      console.error("Error posting data:", error);
    } finally {
      setLoading(false);
      setIsEditing(false);
    }
  };

  // Handler for GST toggle that preserves values
  const handleGstToggle = () => {
    setShowGstFields((prev) => {
      if (prev) {
        // going from true -> false, preserve current GST values
        setPrevGst({ gst_number: firstItem.gst_number, gstDocName: firstItem.gstDocName });
        // do not clear firstItem here — keep values but hide fields. If you want to clear them, uncomment next lines.
        // setFirstItem(item => ({...item, gst_number: '', gstDocName: ''}));
        return false;
      } else {
        // going from false -> true, restore preserved values (if any)
        setFirstItem((item) => ({
          ...item,
          gst_number: (prevGst && prevGst.gst_number) || item.gst_number || "",
          gstDocName: (prevGst && prevGst.gstDocName) || item.gstDocName || "",
        }));
        return true;
      }
    });
  };

  // Helper to determine if fields should be disabled
  const isFieldDisabled = () => {
    // If there's an existing record and not currently editing, fields should be disabled
    return !!firstItem.billingInfo_id && !isEditing;
  };

  return (
    <>
      <Wrapper className="LeadDetails">
        <div className="viewLeadDetails-form">
          <table style={{ width: "100%", color: "#6A6A6A", borderRadius: "5px" }}>
            <tr className="viewLeadDetailsHeading">
              <th style={{ background: "#5B489D", color: "white", fontWeight: "bold" }}>
                <span style={{ fontSize: "18px" }}> Billing Details</span>
              </th>
            </tr>
          </table>
        </div>

        <div style={{ display: "flex", flexDirection: "column" }}>
          <div className="row">
            <div className="col-3">
              <label className="form-label">Billing Name</label>
              <input
                type="text"
                className="form-control"
                name="billingName"
                value={firstItem.billingName}
                onChange={handleChange}
                disabled={isFieldDisabled()}
              />
            </div>
            <div className="col-3">
              <label className="form-label">Contact Number</label>
              <input
                type="number"
                className="form-control"
                name="contactNumber"
                value={firstItem.contactNumber}
                onChange={handleChange}
                disabled={isFieldDisabled()}
              />
            </div>
            <div className="col-6">
              <label className="form-label">Email Id</label>
              <input
                type="text"
                className="form-control"
                name="billingEmail"
                value={firstItem.billingEmail}
                onChange={handleChange}
                disabled={isFieldDisabled()}
              />
            </div>
          </div>

          <div className="row">
            <div className="col-6">
              <label className="form-label">PAN Number</label>
              <input
                type="text"
                className="form-control"
                name="panNumber"
                value={firstItem.panNumber}
                onChange={handleChange}
                disabled={isFieldDisabled()}
              />
            </div>

            <div className="col-3">
              <label className="form-label">PAN Card</label>
              <input
                id="panDocName"
                type="file"
                name="panDocName"
                onChange={handleChangeDoc}
                ref={fileInputRef_PanCard}
                className="form-control"
                style={{ width: "-webkit-fill-available" }}
                disabled={isFieldDisabled()}
              />

              {/* show existing link from server when present */}
              {billingDetailResponse?.panDocName  && (
                <div style={{ marginTop: "5px" }}>
                  <a
                    href={`${baseImageURL}Images/Documents/${billingDetailResponse?.relocation_id}/${billingDetailResponse?.panDocName}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#0096EB", fontWeight: "bold", textDecoration: "none" }}
                  >
                    PAN Card
                  </a>
                </div>
              )}
            </div>
          </div>

          <div className="row">
            <div className="col-2">
              <label className="form-label">GST Registered?</label>
            </div>

            <div className="col-3" style={{ display: "flex" }}>
              <input
                type="checkbox"
                checked={showGstFields}
                onChange={handleGstToggle}
                style={{ marginRight: "6px", marginTop: "-7px" }}
                disabled={isFieldDisabled()}
              />
              <label className="form-label">Do you need GST Invoice?</label>
            </div>
          </div>

          {showGstFields && (
            <div className="row">
              <div className="col-3">
                <label className="form-label">Enter GST No.</label>
                <input
                  type="text"
                  className="form-control"
                  name="gst_number"
                  value={firstItem.gst_number}
                  onChange={handleChange}
                  disabled={isFieldDisabled()}
                />
              </div>

              <div className="col-6">
                <label className="form-label">Upload GST Copy</label>
                <input
                  id="gstDocName"
                  type="file"
                  name="gstDocName"
                  onChange={handleChangeDoc}
                  ref={fileInputRef_GST}
                  className="form-control"
                  style={{ width: "-webkit-fill-available" }}
                  disabled={isFieldDisabled()}
                />
                {billingDetailResponse?.gstDocName  && (
                  <div style={{ marginTop: "5px" }}>
                    <a
                      href={`${baseImageURL}Images/Documents/${billingDetailResponse?.relocation_id}/${billingDetailResponse?.gstDocName}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "#0096EB", fontWeight: "bold", textDecoration: "none" }}
                    >
                      GST Document
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="row">
            <div className="col-12" style={{ display: "flex", justifyContent: "end" }}>
              {/* Edit button now toggles edit mode instead of immediately updating */}
              {firstItem.billingInfo_id && !isEditing && (
                <button
                  onClick={() => setIsEditing(true)}
                  style={{ border: "1px solid black", marginRight: "15px", width: "11%", height: "44px" }}
                >
                  Edit
                </button>
              )}

              {/* Save/Update button. Enabled only when editing or when creating new record */}
              {firstItem.billingInfo_id ? (
                <button
                  onClick={updateDetails}
                  disabled={!isEditing}
                  style={{
                    backgroundColor: !isEditing ? "#ccc" : "#5B489D",
                    color: "#FFFFFF",
                    border: "none",
                    height: "44px",
                    cursor: !isEditing ? "not-allowed" : "pointer",
                  }}
                >
                  {isEditing ? "Save" : "Update"}
                </button>
              ) : (
                <button
                  onClick={uploadDocument}
                  disabled={loading}
                  style={{ backgroundColor: "#5B489D", color: "#FFFFFF", border: "none", height: "44px" }}
                >
                  {loading ? "Uploading..." : "Update"}
                </button>
              )}
            </div>
          </div>

          <br />
        </div>
      </Wrapper>
      <br />
    </>
  );
};

export default AccountsBillingDetails;
